import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Bell, Phone, Mail, MessageCircle, Calendar, Clock, AlertTriangle, ArrowRight, CheckCircle, AlarmClock } from 'lucide-react';
import { toast } from 'sonner';

const TYPE_ICONS = { call: Phone, email: Mail, whatsapp: MessageCircle, meeting: Calendar };
const TYPE_COLORS = {
  call: 'text-emerald-500 bg-emerald-500/10',
  email: 'text-blue-500 bg-blue-500/10',
  whatsapp: 'text-green-500 bg-green-500/10',
  meeting: 'text-violet-500 bg-violet-500/10'
};

const POPUP_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes

export default function FollowupAlertPopup() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [alerts, setAlerts] = useState(null);
  const intervalRef = useRef(null);

  const fetchAlerts = useCallback(async () => {
    try {
      const res = await api.get('/followups/pending-alerts');
      if (res.data.total_pending > 0) {
        setAlerts(res.data);
        setOpen(true);
      } else {
        setAlerts(null);
      }
    } catch (err) {
      /* followup alert fetch - non-critical */
    }
  }, []);

  // Fetch on mount and every 5 minutes
  useEffect(() => {
    fetchAlerts();
    intervalRef.current = setInterval(() => {
      fetchAlerts();
    }, POPUP_INTERVAL_MS);
    return () => clearInterval(intervalRef.current);
  }, [fetchAlerts]);

  const markComplete = async (followupId) => {
    try {
      await api.put(`/followups/${followupId}`, { status: 'completed', completed_at: new Date().toISOString() });
      toast.success('Follow-up marked complete');
      const res = await api.get('/followups/pending-alerts');
      setAlerts(res.data);
      if (res.data.total_pending === 0) setOpen(false);
    } catch {
      toast.error('Failed to update');
    }
  };

  const snoozeFollowup = async (followupId, minutes) => {
    try {
      await api.put(`/followups/${followupId}/snooze?minutes=${minutes}`);
      toast.success(`Snoozed for ${minutes >= 1440 ? '1 day' : minutes >= 60 ? `${minutes/60} hour(s)` : `${minutes} min`}`);
      const res = await api.get('/followups/pending-alerts');
      setAlerts(res.data);
    } catch { toast.error('Failed to snooze'); }
  };

  // When user dismisses, it re-opens after 5 mins if still pending
  const handleDismiss = () => {
    setOpen(false);
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    try {
      const d = new Date(dateStr);
      const now = new Date();
      const diffMs = d - now;
      const diffMins = Math.round(diffMs / 60000);
      const diffHrs = Math.round(diffMs / 3600000);
      if (diffMins < -60) return `${Math.abs(Math.round(diffMs / 3600000))}h overdue`;
      if (diffMins < 0) return `${Math.abs(diffMins)}m overdue`;
      if (diffMins < 60) return `in ${diffMins}m`;
      if (diffHrs < 24) return `in ${diffHrs}h`;
      return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
    } catch {
      return dateStr;
    }
  };

  if (!alerts || alerts.total_pending === 0) return null;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-lg max-h-[80vh]" data-testid="followup-alert-popup">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-voyage-orange/10 flex items-center justify-center">
              <Bell className="w-4 h-4 text-voyage-orange" />
            </div>
            Pending Follow-ups
          </DialogTitle>
          <DialogDescription>
            You have <span className="font-bold text-foreground">{alerts.total_pending}</span> pending follow-up{alerts.total_pending > 1 ? 's' : ''}
            {alerts.overdue > 0 && (
              <span className="text-destructive font-medium"> ({alerts.overdue} overdue)</span>
            )}
            <br />
            <span className="text-xs">This reminder will repeat every 5 minutes until all are marked done.</span>
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-[50vh]">
          <div className="space-y-2 pr-2">
            {alerts.alerts.map((alert) => {
              const Icon = TYPE_ICONS[alert.type] || Phone;
              const colors = TYPE_COLORS[alert.type] || TYPE_COLORS.call;

              return (
                <div
                  key={alert.followup_id}
                  className={`p-3 rounded-xl border transition-all duration-200 ${
                    alert.is_overdue
                      ? 'border-destructive/30 bg-destructive/5'
                      : 'border-border bg-card hover:bg-accent/50'
                  }`}
                  data-testid={`alert-item-${alert.followup_id}`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-9 h-9 rounded-lg ${colors} flex items-center justify-center flex-shrink-0 mt-0.5`}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-heading font-semibold text-sm text-foreground truncate">{alert.client_name}</span>
                        {alert.is_overdue && (
                          <Badge variant="destructive" className="text-[10px] px-1.5 py-0 h-4">
                            <AlertTriangle className="w-2.5 h-2.5 mr-0.5" /> Overdue
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-0.5 text-xs text-muted-foreground">
                        <span className="font-mono">{alert.inquiry_id}</span>
                        {alert.destination && <span>to {alert.destination}</span>}
                        {alert.department && <Badge variant="outline" className="text-[9px] px-1 py-0 h-3.5 capitalize">{alert.department}</Badge>}
                      </div>
                      <div className="flex items-center gap-1 mt-1 text-xs text-muted-foreground">
                        <Clock className="w-3 h-3" />
                        <span className={alert.is_overdue ? 'text-destructive font-medium' : ''}>{formatTime(alert.scheduled_at)}</span>
                      </div>
                      {alert.notes && <p className="text-xs text-muted-foreground mt-1 italic">{alert.notes}</p>}
                    </div>
                    <div className="flex flex-col gap-1 flex-shrink-0">
                      {alert.client_phone && (
                        <a href={`tel:${alert.client_phone}`}
                          data-testid={`call-btn-${alert.followup_id}`}>
                          <Button size="sm" variant="outline" className="h-7 text-xs gap-1 w-full">
                            <Phone className="w-3 h-3" /> Call
                          </Button>
                        </a>
                      )}
                      {alert.client_phone && (
                        <a href={`https://wa.me/${alert.client_phone.replace(/[^0-9]/g, '').length === 10 ? '91' + alert.client_phone.replace(/[^0-9]/g, '') : alert.client_phone.replace(/[^0-9]/g, '')}`}
                          target="_blank" rel="noopener noreferrer"
                          data-testid={`whatsapp-btn-${alert.followup_id}`}>
                          <Button size="sm" className="h-7 text-xs gap-1 w-full bg-[#25D366] hover:bg-[#1da851] text-white border-0">
                            <MessageCircle className="w-3 h-3" /> WhatsApp
                          </Button>
                        </a>
                      )}
                      <Select onValueChange={(v) => snoozeFollowup(alert.followup_id, parseInt(v))}>
                        <SelectTrigger className="h-7 text-[10px] w-[90px]" data-testid={`snooze-btn-${alert.followup_id}`}>
                          <AlarmClock className="w-3 h-3 mr-1" /> Snooze
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="15">15 min</SelectItem>
                          <SelectItem value="30">30 min</SelectItem>
                          <SelectItem value="60">1 hour</SelectItem>
                          <SelectItem value="120">2 hours</SelectItem>
                          <SelectItem value="1440">Tomorrow</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button size="sm" variant="ghost" className="h-7 text-xs gap-1 text-emerald-600"
                        onClick={() => markComplete(alert.followup_id)}
                        data-testid={`done-btn-${alert.followup_id}`}>
                        <CheckCircle className="w-3 h-3" /> Done
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </ScrollArea>

        <div className="flex justify-between items-center pt-2">
          <Button variant="ghost" size="sm" onClick={handleDismiss} className="text-muted-foreground" data-testid="dismiss-alerts-btn">
            Remind me later
          </Button>
          <Button size="sm" className="gap-1 bg-voyage-blue hover:bg-voyage-blue-dark text-white"
            onClick={() => { setOpen(false); navigate('/dashboard/leads'); }}
            data-testid="view-all-leads-btn">
            View All Leads <ArrowRight className="w-3 h-3" />
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
